import React, { Component, forwardRef, MouseEvent, ReactElement, Ref } from 'react';
import { createPortal } from 'react-dom';

import ReactResizeDetector from 'react-resize-detector';

import { Placement } from '@philips-emr/tws-constants';
import { arrayOf, bool, element, func, number, object, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { CloseEvent } from '@philips-emr/tws-core/lib/component/types';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { classNames, isEnumValid, Position } from '@philips-emr/tws-utils';

export interface PopoverContentProps {
  children: ReactElement | ReactElement[];
  className?: string;
  document?: Document;
  innerRef?: Ref<HTMLDivElement>;
  maxHeight?: number;
  minWidth?: number;
  onClose?: (event: CloseEvent) => void;
  onResize?: (width: number, height: number) => void;
  open?: boolean;
  padding?: number;
  placement?: Placement;
  position?: Position;
  showArrow?: boolean;
  title?: string;
  zIndex?: number;
}

class PopoverContent extends Component<PopoverContentProps> {

  static propTypes = {
    children: oneOfType([element, arrayOf(element)]),
    className: string,
    document: object,
    innerRef: func,
    maxHeight: number,
    minWidth: number,
    onClose: func,
    onResize: func,
    open: bool,
    padding: number,
    placement: string,
    position: object,
    showArrow: bool,
    title: string,
    zIndex: number,
  };

  static defaultProps = {
    placement: Placement.BOTTOM,
    position: {},
    showArrow: true,
  };

  getClassName(): string {
    const { placement, className } = this.props;

    return classNames({
      'c-popover': true,
      [`c-popover--${placement}`]: isEnumValid(Placement, placement),
      [`${className}`]: Boolean(className),
    });
  }

  onClick = (event: MouseEvent<HTMLDivElement>, shouldClose = false): void => {
    const { onClose, open } = this.props;

    if (open && shouldClose) {
      onClose && onClose(new CloseEvent());
    }

    event.stopPropagation();
  };

  renderContent(): ReactElement {
    const { children, innerRef, maxHeight, minWidth, onResize, padding, position, showArrow, title, zIndex } = this.props;
    const { arrow, guest } = position || {};

    return (
      <div className={this.getClassName()} ref={innerRef} style={{ ...guest, zIndex }}>
        <div className="c-popover__overlay" onClick={event => this.onClick(event, true)} />

        {showArrow && <div className="c-popover__arrow" onClick={event => this.onClick(event)} style={{ ...arrow }} />}

        <div className="c-popover__content" onClick={this.onClick} style={{ minWidth, maxHeight, padding }}>
          <ReactResizeDetector handleWidth handleHeight skipOnMount onResize={onResize} />

          {title && <div className="c-popover__title">{msg(title)}</div>}

          {children}
        </div>
      </div>
    );
  }

  render(): ReactElement | null {
    const { document } = this.props;

    if (document?.body) {
      return createPortal(this.renderContent(), document.body);
    }
    return null;
  }
}

const popoverContentWithRef = forwardRef<HTMLDivElement, PopoverContentProps>((props, ref) => {
  return <PopoverContent {...props} innerRef={ref} />;
});

popoverContentWithRef.displayName = PopoverContent.name;

export default popoverContentWithRef;
